/**
 * Listagem de espelhos de ponto com filtros e cálculo sob demanda.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { espApi, funcApi, getErrorMessage } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import { formatCpf, formatHoras } from '../../types/api'
import type { EspelhoStatusValue } from '../../types/api'

const STATUS_BADGE: Record<EspelhoStatusValue, { label: string; cls: string }> = {
  ABERTO: { label: 'Aberto', cls: 'badge-yellow' },
  EM_TRATAMENTO: { label: 'Em tratamento', cls: 'badge-blue' },
  FECHADO: { label: 'Fechado', cls: 'badge-gray' },
}

export function EspelhoPage() {
  const qc = useQueryClient()
  const today = new Date().toISOString().slice(0, 10)
  const firstDay = today.slice(0, 8) + '01'

  const [filters, setFilters] = useState({ data_inicio: firstDay, data_fim: today, status: '', cpf_pis: '' })
  const [calcForm, setCalcForm] = useState({ cpf_pis: '', data: today })
  const [calcMsg, setCalcMsg] = useState<string | null>(null)

  const { data: espelhos, isLoading } = useQuery({
    queryKey: ['espelhos', filters],
    queryFn: () => espApi.list({
      data_inicio: filters.data_inicio || undefined,
      data_fim: filters.data_fim || undefined,
      status: filters.status || undefined,
      cpf_pis: filters.cpf_pis ? Number(filters.cpf_pis) : undefined,
    }),
  })

  const { data: funcs } = useQuery({ queryKey: ['funcionarios', true], queryFn: () => funcApi.list(true) })

  const calcMutation = useMutation({
    mutationFn: () => espApi.calcular(Number(calcForm.cpf_pis), calcForm.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['espelhos'] }); setCalcMsg('Espelho calculado com sucesso.') },
    onError: (err) => setCalcMsg('Erro: ' + getErrorMessage(err)),
  })

  const fecharMutation = useMutation({
    mutationFn: (id: string) => espApi.fechar(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['espelhos'] }),
  })

  return (
    <div>
      <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, marginBottom: 'var(--space-6)' }}>Espelho de Ponto</h1>

      {/* Painel de cálculo */}
      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-4)' }}>Calcular espelho</h2>
        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>Funcionário</label>
            <select className="input" style={{ minWidth: 220 }} value={calcForm.cpf_pis} onChange={e => setCalcForm(f => ({ ...f, cpf_pis: e.target.value }))}>
              <option value="">Selecione…</option>
              {funcs?.map(f => <option key={f.id} value={String(f.cpf)}>{f.nome} ({formatCpf(f.cpf)})</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>Data</label>
            <input className="input" type="date" value={calcForm.data} onChange={e => setCalcForm(f => ({ ...f, data: e.target.value }))} />
          </div>
          <button className="btn btn-primary" onClick={() => { setCalcMsg(null); calcMutation.mutate() }}
            disabled={calcMutation.isPending || !calcForm.cpf_pis}>
            {calcMutation.isPending ? 'Calculando…' : 'Calcular'}
          </button>
        </div>
        {calcMsg && <div className={`alert ${calcMsg.startsWith('Erro') ? 'alert-danger' : 'alert-success'}`} style={{ marginTop: 'var(--space-3)' }}>{calcMsg}</div>}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>De</label>
          <input className="input" type="date" value={filters.data_inicio} onChange={e => setFilters(f => ({ ...f, data_inicio: e.target.value }))} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>Até</label>
          <input className="input" type="date" value={filters.data_fim} onChange={e => setFilters(f => ({ ...f, data_fim: e.target.value }))} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>Status</label>
          <select className="input" value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}>
            <option value="">Todos</option>
            <option value="ABERTO">Aberto</option>
            <option value="EM_TRATAMENTO">Em tratamento</option>
            <option value="FECHADO">Fechado</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-1)' }}>CPF</label>
          <input className="input" placeholder="Filtrar por CPF" value={filters.cpf_pis} onChange={e => setFilters(f => ({ ...f, cpf_pis: e.target.value.replace(/\D/g, '') }))} style={{ width: 140 }} />
        </div>
      </div>

      {/* Tabela */}
      <div className="card" style={{ padding: isLoading || !espelhos?.length ? 'var(--space-6)' : 0 }}>
        {isLoading ? <PageLoading /> : !espelhos?.length ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-12)' }}>
            <p style={{ color: 'var(--color-gray-400)', fontSize: 'var(--font-size-sm)' }}>
              Nenhum espelho no período. Use "Calcular espelho" acima para gerar.
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>CPF/PIS</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th>Normais</th>
                  <th>Extras</th>
                  <th>Noturnas</th>
                  <th>Falta</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {espelhos.map(e => {
                  const badge = STATUS_BADGE[e.status] ?? { label: e.status, cls: 'badge-gray' }
                  return (
                    <tr key={e.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{formatCpf(e.funcionario_cpf_pis)}</td>
                      <td>{new Date(e.data + 'T12:00:00').toLocaleDateString('pt-BR')}</td>
                      <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                      <td style={{ color: 'var(--color-success)', fontWeight: 500 }}>{formatHoras(e.horas_normais_s)}</td>
                      <td style={{ color: 'var(--color-primary)' }}>{formatHoras(e.horas_extras_s)}</td>
                      <td style={{ color: 'var(--color-warning)' }}>{formatHoras(e.horas_noturnas_s)}</td>
                      <td style={{ color: 'var(--color-danger)' }}>{formatHoras(e.horas_falta_s)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <Link to={`/rh/espelho/${e.id}`} className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', padding: 'var(--space-1) var(--space-2)' }}>
                            Detalhe
                          </Link>
                          {e.status !== 'FECHADO' && (
                            <button className="btn btn-ghost" style={{ fontSize: 'var(--font-size-xs)', padding: 'var(--space-1) var(--space-2)' }}
                              onClick={() => { if (confirm('Fechar este espelho?')) fecharMutation.mutate(e.id) }}>
                              Fechar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
